/**
 * Progress Boost Report (mdexam_legacy, super_admin)
 *
 * Monitors the weekly check-in funnel from Segment events piped to BigQuery.
 * - Sankey of branch flow + stacked funnel bars
 * - Paginated list of check-in sessions with full journey detail on click
 */

import { format } from 'date-fns'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Loader2, AlertCircle, X } from 'lucide-react'
import { progressBoostAPI } from '../services/api'
import SankeyDiagram from '../components/SankeyDiagram'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Button, Input, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui'

const OUTCOME_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Completed (reached /reminder)', value: 'completed' },
  { label: 'Dropped off', value: 'dropped' },
]

const PAGE_SIZE = 50

function todayISO() {
  return format(new Date(), 'yyyy-MM-dd')
}
function daysAgoISO(n) {
  return format(new Date(Date.now() - n * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
}

function fmtPct(v) {
  if (v == null) return '–'
  return `${(v * 100).toFixed(1)}%`
}
function fmtNum(v) {
  if (v == null) return '–'
  return Number(v).toLocaleString()
}
function fmtDateTime(iso) {
  if (!iso) return '–'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}
function fmtDuration(seconds) {
  if (seconds == null) return '–'
  const s = Math.round(seconds)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  if (m < 60) return `${m}m ${rem}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

function Metric({ label, value, sub }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  )
}

function Spinner({ label = 'Loading…' }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  )
}

function ErrorBox({ message }) {
  return (
    <div className="flex items-center gap-2 text-destructive py-4">
      <AlertCircle className="h-4 w-4" /> {message}
    </div>
  )
}

/* ───── Branching Funnel ─────────────────────────────────────────────── */

// Row-based layout of the Progress Boost flow. Each row is either one step or
// a set of parallel branches. Mirrors the documented flow at
// mdexam-dashboard/docs/progress-boost-flow.html.
const FUNNEL_ROWS = [
  ['/weekly-check'],
  ['/today-weight'],
  ['/which-medicine'],
  ['/other-medicine', '/every-day'],
  ['/what-happen'],
  ['/last-medicine'],
  ['/units-count'],
  ['/side-effects'],
  ['/side-effect-other'],
  ['/lost-weight/high', '/lost-weight/medium', '/lost-weight/low', '/lost-weight/steady', '/lost-weight/gained'],
  ['/medical-history'],
  ['/hungry'],
  ['/physically-active'],
  ['/stress-level', '/daily-stress-level'],
  ['/water'],
  ['/staying-hydrated'],
  ['/soda'],
  ['/sleep-track'],
  ['/feedback/nps'],
  ['/feedback/goal-driver'],
  ['/feedback/reason'],
  ['/feedback/helpful'],
  ['/feedback/friction'],
  ['/feedback/value'],
  ['/feedback/wishlist'],
  ['/feedback/continue'],
  ['/feedback/improve'],
  ['/picture'],
  ['/confirm-next-order'],
  ['/reminder'],
]

function stepUsersMap(data) {
  const map = new Map()
  for (const s of data?.steps || []) {
    const rel = s.step.startsWith('/weekly-check') ? s.step.slice('/weekly-check'.length) || '/weekly-check' : s.step
    map.set(rel === '' ? '/weekly-check' : rel, s.users)
  }
  return map
}

function deltaColor(delta) {
  if (delta == null || !Number.isFinite(delta)) return 'bg-blue-500'
  if (delta >= -0.1) return 'bg-emerald-500'
  if (delta >= -0.3) return 'bg-amber-500'
  return 'bg-rose-500'
}
function deltaText(delta) {
  if (delta == null || !Number.isFinite(delta)) return null
  if (delta > -0.005 && delta < 0.005) return { text: '— 0%', tone: 'muted' }
  const pct = (delta * 100).toFixed(0)
  if (delta < -0.1) return { text: `↘ ${pct}%`, tone: 'down' }
  if (delta > 0.1) return { text: `↗ +${pct}%`, tone: 'up' }
  return { text: `${delta < 0 ? '↘' : '↗'} ${pct}%`, tone: 'muted' }
}

function BranchingFunnel({ data }) {
  if (!data?.steps?.length) return <p className="text-muted-foreground text-sm">No funnel data.</p>
  const usersByStep = stepUsersMap(data)
  const splash = data.splash_users || 1

  // Compute previous-row max users for delta calc (skip rows where no step appears in data).
  const rowSummaries = FUNNEL_ROWS.map((row) => {
    const steps = row
      .map((s) => ({ step: s, users: usersByStep.get(s) ?? null }))
      .filter((s) => s.users != null)
    return { row, steps, maxUsers: steps.reduce((m, s) => Math.max(m, s.users || 0), 0) }
  })

  return (
    <div className="relative">
      <div className="absolute left-1/2 top-2 bottom-2 w-px bg-border" aria-hidden />
      <div className="space-y-2 relative">
        {rowSummaries.map((rs, i) => {
          if (rs.steps.length === 0) return null
          // Find the most recent non-empty previous row for delta baseline.
          let prevMax = null
          for (let j = i - 1; j >= 0; j--) {
            if (rowSummaries[j].steps.length > 0) { prevMax = rowSummaries[j].maxUsers; break }
          }
          const isBranch = rs.steps.length > 1
          return (
            <div key={i} className="flex gap-2">
              {rs.steps.map((s, idx) => {
                const pct = s.users / splash
                const delta = prevMax ? (s.users - prevMax) / prevMax : null
                const dtxt = deltaText(delta)
                const barClass = deltaColor(delta)
                return (
                  <div
                    key={s.step}
                    className="flex items-center gap-3 text-sm bg-card border rounded-md px-2 py-1.5"
                    style={{ flex: isBranch ? `1 1 0` : '1 1 100%' }}
                  >
                    {isBranch && idx === 0 && (
                      <span className="text-violet-500 text-xs" title="Branch start">⑃</span>
                    )}
                    <div className="w-48 shrink-0 truncate font-mono text-xs">{s.step}</div>
                    <div className="flex-1 bg-muted rounded-sm h-5 relative overflow-hidden min-w-[60px]">
                      <div
                        className={`absolute inset-y-0 left-0 ${barClass}`}
                        style={{ width: `${Math.max(pct * 100, 1)}%` }}
                      />
                      <div className="absolute inset-0 flex items-center pl-2 gap-2 text-[11px] text-white mix-blend-difference">
                        <span className="tabular-nums font-semibold">{fmtNum(s.users)}</span>
                        <span className="opacity-80">{fmtPct(pct)} of start</span>
                      </div>
                    </div>
                    <div
                      className={`w-20 text-right tabular-nums text-xs whitespace-nowrap ${
                        dtxt?.tone === 'down' ? 'text-rose-600' :
                        dtxt?.tone === 'up' ? 'text-emerald-600' : 'text-muted-foreground'
                      }`}
                    >
                      {dtxt?.text || '–'}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ───── Sankey conversion (backend → nivo) ───────────────────────────── */

// nivo's sankey requires a DAG. Real session flow has back-edges (users navigate
// backward), so we break cycles by repeatedly dropping the smallest-weight edge
// that participates in a cycle until the graph is acyclic.
function breakCycles(links) {
  const edges = links.map((l) => ({ ...l }))

  while (true) {
    const nodeSet = new Set()
    for (const e of edges) { nodeSet.add(e.source); nodeSet.add(e.target) }
    const inDeg = new Map([...nodeSet].map((n) => [n, 0]))
    for (const e of edges) inDeg.set(e.target, (inDeg.get(e.target) || 0) + 1)

    const queue = [...inDeg].filter(([, d]) => d === 0).map(([n]) => n)
    const visited = new Set()
    const adj = new Map()
    for (const e of edges) {
      if (!adj.has(e.source)) adj.set(e.source, [])
      adj.get(e.source).push(e)
    }
    while (queue.length) {
      const n = queue.shift()
      if (visited.has(n)) continue
      visited.add(n)
      for (const e of (adj.get(n) || [])) {
        inDeg.set(e.target, inDeg.get(e.target) - 1)
        if (inDeg.get(e.target) === 0) queue.push(e.target)
      }
    }

    if (visited.size === nodeSet.size) return edges

    // At least one cycle remains. Drop the smallest-weight edge that lives
    // entirely inside the still-unvisited set (those are the cyclic nodes).
    let drop = -1
    for (let i = 0; i < edges.length; i++) {
      const e = edges[i]
      if (!visited.has(e.source) && !visited.has(e.target)) {
        if (drop < 0 || edges[i].value < edges[drop].value) drop = i
      }
    }
    if (drop < 0) return edges // safety
    edges.splice(drop, 1)
  }
}

function toSankeyData(flow) {
  if (!flow?.nodes?.length || !flow?.links?.length) return null

  const dagLinks = breakCycles(
    flow.links.map((l) => ({ source: l.sourceName, target: l.targetName, value: l.value })),
  )
  // Trim node list to only those still referenced.
  const used = new Set()
  for (const l of dagLinks) { used.add(l.source); used.add(l.target) }
  const nodes = [...used].map((name) => ({
    id: name,
    group: name === '__drop_off__' ? 'not_converted' : 'flow',
  }))
  return { nodes, links: dagLinks }
}

/* ───── Journey list ─────────────────────────────────────────────────── */

function JourneyTable({ rows, onPick, loading, hasMore, onMore }) {
  if (loading && !rows.length) return <Spinner label="Loading journeys…" />
  if (!rows.length) return <p className="text-muted-foreground text-sm py-4">No check-ins in this range.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground border-b">
          <tr>
            <th className="py-2 px-2">Started</th>
            <th className="py-2 px-2">User</th>
            <th className="py-2 px-2">Email</th>
            <th className="py-2 px-2 text-right">Steps</th>
            <th className="py-2 px-2">Last step</th>
            <th className="py-2 px-2">Outcome</th>
            <th className="py-2 px-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.session_id} className="border-b hover:bg-muted/40">
              <td className="py-2 px-2 text-xs whitespace-nowrap">{fmtDateTime(r.started_at)}</td>
              <td className="py-2 px-2 text-xs font-mono truncate max-w-[160px]">{r.user_id?.slice(0, 12)}…</td>
              <td className="py-2 px-2 text-xs truncate max-w-[220px]">{r.email || '–'}</td>
              <td className="py-2 px-2 text-right tabular-nums">{r.step_count}</td>
              <td className="py-2 px-2 text-xs font-mono truncate max-w-[260px]">{r.last_step}</td>
              <td className="py-2 px-2">
                <Badge variant={r.outcome === 'completed' ? 'default' : 'secondary'}>
                  {r.outcome}
                </Badge>
              </td>
              <td className="py-2 px-2">
                <Button size="sm" variant="ghost" onClick={() => onPick(r.session_id)}>View</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="flex justify-center py-3">
          <Button variant="outline" size="sm" onClick={onMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}

/* ───── Journey detail ───────────────────────────────────────────────── */

function JourneyDetail({ sessionId, start, end, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!sessionId) return
    setLoading(true)
    setError(null)
    progressBoostAPI
      .getJourneyDetail(sessionId, { start, end })
      .then((res) => {
        if (res.data?.success) setData(res.data.data)
        else throw new Error(res.data?.error?.message || 'Request failed')
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [sessionId, start, end])

  return (
    <Dialog open={!!sessionId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Check-in journey</span>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </DialogTitle>
        </DialogHeader>

        {loading && <Spinner />}
        {error && <ErrorBox message={error} />}
        {data && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Started:</span> {fmtDateTime(data.started_at)}</div>
              <div><span className="text-muted-foreground">Ended:</span> {fmtDateTime(data.ended_at)}</div>
              <div><span className="text-muted-foreground">Outcome:</span> <Badge>{data.outcome}</Badge></div>
              <div><span className="text-muted-foreground">Last step:</span> <code className="text-xs">{data.last_step}</code></div>
              {data.submitted_at && (
                <div><span className="text-muted-foreground">Submitted:</span> {fmtDateTime(data.submitted_at)}</div>
              )}
              {data.completed_at && (
                <div><span className="text-muted-foreground">Completed:</span> {fmtDateTime(data.completed_at)}</div>
              )}
            </div>

            {data.customer && (
              <div className="border rounded-md p-3 bg-muted/30 text-sm">
                <div className="font-semibold mb-1">Customer (mdexam_legacy)</div>
                {data.customer._not_found ? (
                  <div className="text-muted-foreground">
                    No user matched email <code>{data.customer._lookup_email}</code> in users collection
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div><span className="text-muted-foreground">Email:</span> {data.customer.email}</div>
                    <div><span className="text-muted-foreground">Name:</span> {[data.customer.firstName, data.customer.lastName].filter(Boolean).join(' ') || '–'}</div>
                    <div><span className="text-muted-foreground">State:</span> {data.customer.state || '–'}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {data.customer.phoneNumber || '–'}</div>
                    <div><span className="text-muted-foreground">Sub status:</span> {data.customer.subscriptionStatus || '–'}</div>
                    <div><span className="text-muted-foreground">Weight:</span> {data.customer.currentWeight || '–'}</div>
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="font-semibold text-sm mb-2">Timeline ({data.timeline.length} events)</div>
              <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-2">
                {data.timeline.map((e, i) => (
                  <div key={i} className="flex gap-3 text-xs font-mono py-1 border-b border-dashed">
                    <span className="text-muted-foreground w-44 shrink-0">{fmtDateTime(e.timestamp)}</span>
                    {e.kind === 'page' ? (
                      <span className="text-blue-700 dark:text-blue-400">page · {e.step}</span>
                    ) : (
                      <details className="flex-1">
                        <summary className="text-green-700 dark:text-green-400 cursor-pointer">
                          track · {e.event}
                        </summary>
                        <pre className="mt-1 bg-muted/40 p-2 rounded text-[10px] whitespace-pre-wrap break-all">
                          {JSON.stringify(e.payload, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/* ───── Page shell ───────────────────────────────────────────────────── */

export default function ProgressBoostPage() {
  const [start, setStart] = useState(daysAgoISO(30))
  const [end, setEnd] = useState(todayISO())
  const [outcome, setOutcome] = useState('all')
  const [minWeight, setMinWeight] = useState(10)

  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryErr, setSummaryErr] = useState(null)
  const [funnel, setFunnel] = useState(null)
  const [flow, setFlow] = useState(null)
  const [journeys, setJourneys] = useState([])
  const [journeysLoading, setJourneysLoading] = useState(false)
  const [funnelLoading, setFunnelLoading] = useState(false)
  const [flowLoading, setFlowLoading] = useState(false)
  const [funnelErr, setFunnelErr] = useState(null)
  const [flowErr, setFlowErr] = useState(null)
  const [journeysErr, setJourneysErr] = useState(null)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [selectedSessionId, setSelectedSessionId] = useState(null)

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true); setSummaryErr(null)
    try {
      const res = await progressBoostAPI.getSummary({ start, end })
      if (res.data?.success) setSummary(res.data.data)
      else throw new Error(res.data?.error?.message || 'Failed')
    } catch (e) { setSummaryErr(e.message) } finally { setSummaryLoading(false) }
  }, [start, end])

  const loadFunnel = useCallback(async () => {
    setFunnelLoading(true); setFunnelErr(null)
    try {
      const res = await progressBoostAPI.getFunnel({ start, end })
      if (res.data?.success) setFunnel(res.data.data)
      else throw new Error(res.data?.error?.message || 'Failed')
    } catch (e) { setFunnelErr(e.message) } finally { setFunnelLoading(false) }
  }, [start, end])

  const loadFlow = useCallback(async () => {
    setFlowLoading(true); setFlowErr(null)
    try {
      const res = await progressBoostAPI.getFlow({ start, end, minWeight })
      if (res.data?.success) setFlow(res.data.data)
      else throw new Error(res.data?.error?.message || 'Failed')
    } catch (e) { setFlowErr(e.message) } finally { setFlowLoading(false) }
  }, [start, end, minWeight])

  const loadJourneys = useCallback(async (resetOffset = true) => {
    setJourneysLoading(true); setJourneysErr(null)
    try {
      const off = resetOffset ? 0 : offset
      const params = { start, end, limit: PAGE_SIZE, offset: off }
      if (outcome !== 'all') params.outcome = outcome
      const res = await progressBoostAPI.getJourneys(params)
      if (res.data?.success) {
        const { journeys: items, has_more } = res.data.data
        setJourneys((prev) => (resetOffset ? items : [...prev, ...items]))
        setOffset(off + items.length)
        setHasMore(has_more)
      } else throw new Error(res.data?.error?.message || 'Failed')
    } catch (e) { setJourneysErr(e.message) } finally { setJourneysLoading(false) }
  }, [start, end, outcome, offset])

  useEffect(() => { loadSummary() }, [loadSummary])
  useEffect(() => { loadFunnel() }, [loadFunnel])
  useEffect(() => { loadFlow() }, [loadFlow])
  useEffect(() => { loadJourneys(true) /* reset on filter change */ }, [start, end, outcome]) // eslint-disable-line

  const sankeyData = useMemo(() => toSankeyData(flow), [flow])

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Progress Boost</h1>
        <p className="text-muted-foreground text-sm">
          Weekly check-in funnel for mdexam_legacy. Page + track events from Segment (BigQuery <code>portal</code>),
          sessionized with a {30}-min inactivity gap.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Start</div>
            <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">End</div>
            <Input type="date" value={end} min={start} max={todayISO()} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Outcome</div>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTCOME_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Sankey min weight</div>
            <Input
              type="number"
              min={1}
              value={minWeight}
              onChange={(e) => setMinWeight(Math.max(1, Number(e.target.value) || 1))}
              className="w-[120px]"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setStart(daysAgoISO(7)); setEnd(todayISO()) }}>7d</Button>
            <Button size="sm" variant="outline" onClick={() => { setStart(daysAgoISO(30)); setEnd(todayISO()) }}>30d</Button>
            <Button size="sm" variant="outline" onClick={() => { setStart(daysAgoISO(90)); setEnd(todayISO()) }}>90d</Button>
          </div>
        </CardContent>
      </Card>

      {summaryErr ? <ErrorBox message={summaryErr} /> : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Metric
            label="Check-ins started"
            value={summaryLoading ? '…' : fmtNum(summary?.sessions_started)}
            sub={summary ? `${fmtNum(summary.unique_users)} unique users` : null}
          />
          <Metric
            label="Completed"
            value={summaryLoading ? '…' : fmtNum(summary?.sessions_completed)}
            sub={summary ? `${fmtPct(summary.completion_rate)} of started` : null}
          />
          <Metric
            label="Dropped off"
            value={summaryLoading ? '…' : fmtNum((summary?.sessions_started || 0) - (summary?.sessions_completed || 0))}
            sub={summary ? `${fmtPct(1 - (summary.completion_rate || 0))} of started` : null}
          />
          <Metric
            label="Avg steps / check-in"
            value={summaryLoading ? '…' : (summary?.avg_steps ? summary.avg_steps.toFixed(1) : '–')}
            sub={summary?.median_steps ? `median ${summary.median_steps}` : null}
          />
          <Metric
            label="Median time to complete"
            value={summaryLoading ? '…' : fmtDuration(summary?.median_completion_seconds)}
            sub={summary?.avg_completion_seconds ? `avg ${fmtDuration(summary.avg_completion_seconds)}` : null}
          />
          <Metric
            label="Top drop-off step"
            value={summaryLoading ? '…' : (summary?.top_drop_offs?.[0]?.step?.replace('/weekly-check/', '') || '–')}
            sub={summary?.top_drop_offs?.[0] ? `${fmtNum(summary.top_drop_offs[0].sessions)} sessions` : null}
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Branching flow</CardTitle>
          <CardDescription>
            User transitions step-to-step within a session, top → bottom. Drop-offs end at <code>__drop_off__</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {flowErr && <ErrorBox message={flowErr} />}
          {!flowErr && (
            <SankeyDiagram
              data={sankeyData}
              type="flow"
              height={900}
              layout="vertical"
              loading={flowLoading}
              emptyMessage="No transitions in this range."
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Step funnel</CardTitle>
          <CardDescription>Unique users hitting each Progress Boost page in canonical order.</CardDescription>
        </CardHeader>
        <CardContent>
          {funnelLoading ? <Spinner /> : funnelErr ? <ErrorBox message={funnelErr} /> : <BranchingFunnel data={funnel} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Check-in journeys</CardTitle>
          <CardDescription>One row per sessionized check-in. Click a row to see the full timeline.</CardDescription>
        </CardHeader>
        <CardContent>
          {journeysErr && <ErrorBox message={journeysErr} />}
          <JourneyTable
            rows={journeys}
            loading={journeysLoading}
            hasMore={hasMore}
            onMore={() => loadJourneys(false)}
            onPick={setSelectedSessionId}
          />
        </CardContent>
      </Card>

      <JourneyDetail
        sessionId={selectedSessionId}
        start={start}
        end={end}
        onClose={() => setSelectedSessionId(null)}
      />
    </div>
  )
}
